import { useRef } from "react";

import {
  type Logger,
  type LoggerConfig,
  type AmplitudeConfig,
  type LoggerOptions,
} from "../analytics";
import { type AbstractUser } from "../user";

import { useDeepEqualEffect } from "./use-deep-equal";

type UseLoggerHook<U extends AbstractUser, A extends AmplitudeConfig> = {
  <O extends LoggerOptions>(user: U, options: O): Logger<U, O, A>;
  <O extends LoggerOptions>(
    name: string | undefined,
    user: U,
    options: O,
  ): Logger<U, O, A>;
};

export const createLoggerHook = <
  U extends AbstractUser,
  /* The type of the Amplitude configuration is irrelevant as far as the hook is concerned, and it
     simplifies usage and typing to remove. */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  A extends AmplitudeConfig = any,
>(
  config: LoggerConfig<readonly string[], U, A>,
): UseLoggerHook<U, A> => {
  function useLogger<O extends LoggerOptions>(
    user: U,
    options: O,
  ): Logger<U, O, A>;

  function useLogger<O extends LoggerOptions>(
    name: string | undefined,
    user: U,
    options: O,
  ): Logger<U, O, A>;

  function useLogger<O extends LoggerOptions>(
    arg0: string | undefined | U,
    arg1: U | O,
    arg2?: O,
  ) {
    const firstRendered = useRef<boolean>(false);

    let name: string;
    let user: U;
    let options: O;
    if (typeof arg0 === "string" || typeof arg0 === "undefined") {
      name = arg0 || config.defaultName;
      user = arg1 as U;
      if (arg2 === undefined) {
        throw new TypeError("Anvalid function signature implementation!");
      }
      options = arg2;
    } else {
      name = config.defaultName;
      user = arg0 as U;
      options = arg1 as O;
    }

    const loggerRef = useRef<Logger<U, O, A>>(
      config.createLogger(name, options),
    );

    useDeepEqualEffect(() => {
      /* Setting the user on the Logger instance will cause the Amplitude instance to re-identify
         with the updated user on the next call to logger.event. */
      loggerRef.current.user = user;
    }, [user]);

    useDeepEqualEffect(() => {
      if (firstRendered.current === true) {
        loggerRef.current.options = options;
      }
    }, [options]);

    firstRendered.current = true;

    return loggerRef.current;
  }

  return useLogger;
};
